import { useAuthStore } from "@/store/auth.store";
import { type AuthHeader, useAuthHeader } from "@roman/shared/hooks/useAuthHeader";
import romanApi, { type RomanApiError } from "@roman/shared/lib/net/romanApi";
import type { Language, LanguageQuery } from "@roman/shared/types/Language";
import type { UserRoundEntryUnit } from "@roman/shared/types/UserRoundEntryUnit";
import { useEffect } from "react";
import useSWR from "swr";

export interface UserRoundEntryUnitsResponse<T extends UserRoundEntryUnit> {
  userRoundEntryUnits: T[];
}

const fetcher = <T extends UserRoundEntryUnit>(url: string, headers: AuthHeader, status: string, lang: Language) =>
  romanApi.get<UserRoundEntryUnitsResponse<T>, LanguageQuery & { status: string }>(url, { status, lang }, { headers });

export const useWebUserRoundEntryUnits = <T extends UserRoundEntryUnit>(
  status: "schedule" | "live" | "prizeDistributed" | "cancelled",
  handleError: (err: RomanApiError) => void,
  lang: Language = "en",
  pollInterval?: number,
) => {
  const header = useAuthHeader();
  const authenticated = useAuthStore((state) => state.authState === "authenticated");

  const { data, error, isLoading, mutate, isValidating } = useSWR<UserRoundEntryUnitsResponse<T>>(
    authenticated ? ["/web/soccer/roundEntryUnits", status] : null,
    ([url, status]: [string, string]) => fetcher(url, header, status, lang),
    {
      refreshInterval: authenticated ? pollInterval : undefined,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: authenticated ? pollInterval : undefined,
    },
  );

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  return {
    data: data ?? { userRoundEntryUnits: [] },
    mutate,
    isLoading: isLoading || isValidating,
  };
};
